import { create } from 'zustand';
import type {
  PsycheThought,
  PsycheConfig,
  PsycheVoice,
  PsycheDepth,
  DeliveredThought,
  ThoughtIntensity,
} from '@machina/core';
import { trpc } from '../lib/trpc';
import { createMultiSubscription } from '../lib/realtimeHelpers';

/**
 * Psyche Store
 *
 * Manages the GM-curated psyche voice system.
 *
 * GM side: thought queue (pending/approved/edited), generate, approve/reject/edit/deliver.
 * Player side: delivered thoughts, mark as read.
 * Realtime: players receive thoughts via Supabase Realtime on delivery.
 */

interface PsycheState {
  // Config (per-campaign settings)
  config: PsycheConfig;

  // GM: thought queue
  thoughtQueue: PsycheThought[];
  thoughtHistory: PsycheThought[];

  // Player: delivered thoughts
  deliveredThoughts: DeliveredThought[];

  // Loading
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;

  // Realtime
  realtimeCleanup: (() => void) | null;

  // Actions — Config
  setDepth: (depth: PsycheDepth) => void;
  toggleVoice: (voice: PsycheVoice) => void;

  // Actions — GM Queue
  loadQueue: (campaignId: string) => Promise<void>;
  loadHistory: (campaignId: string) => Promise<void>;
  generateThoughts: (
    campaignId: string,
    activeScene: string,
    options?: {
      recentEvents?: string[];
      characterContext?: {
        id: string;
        name: string;
        personalityTraits?: string;
        ideals?: string;
        bonds?: string;
        flaws?: string;
        background?: string;
      };
    }
  ) => Promise<{ count: number; narratorNote?: string }>;
  createThought: (
    campaignId: string,
    voice: PsycheVoice,
    content: string,
    options?: {
      context?: string;
      intensity?: ThoughtIntensity;
      targetCharacterId?: string;
      factionRelevance?: string;
    }
  ) => Promise<void>;
  approveThought: (thoughtId: string, targetCharacterId?: string) => Promise<void>;
  rejectThought: (thoughtId: string) => Promise<void>;
  editThought: (
    thoughtId: string,
    content: string,
    options?: { intensity?: ThoughtIntensity; voice?: PsycheVoice }
  ) => Promise<void>;
  deliverThought: (thoughtId: string, deliverTo: string) => Promise<void>;

  // Actions — Player
  loadDelivered: (campaignId: string, characterId: string) => Promise<void>;
  markRead: (thoughtId: string) => Promise<void>;

  // Realtime
  subscribeToRealtime: (campaignId: string, characterId?: string) => void;
  unsubscribeFromRealtime: () => void;
}

const DEFAULT_CONFIG: PsycheConfig = {
  depth: 'ATMOSPHERIC',
  enabledVoices: [
    'EMPATHY', 'LOGIC', 'INSTINCT', 'RHETORIC',
    'PERCEPTION', 'MEMORY', 'VOLITION', 'SUGGESTION',
  ],
  voiceFrequency: 50,
  perCharacterOverrides: {},
};

export const usePsycheStore = create<PsycheState>((set, get) => ({
  config: DEFAULT_CONFIG,
  thoughtQueue: [],
  thoughtHistory: [],
  deliveredThoughts: [],
  isLoading: false,
  isGenerating: false,
  error: null,
  realtimeCleanup: null,

  // ==========================================================================
  // CONFIG
  // ==========================================================================

  setDepth: (depth) => {
    set((state) => ({
      config: { ...state.config, depth },
    }));
  },

  toggleVoice: (voice) => {
    set((state) => {
      const enabled = state.config.enabledVoices;
      const isEnabled = enabled.includes(voice);
      return {
        config: {
          ...state.config,
          enabledVoices: isEnabled
            ? enabled.filter((v) => v !== voice)
            : [...enabled, voice],
        },
      };
    });
  },

  // ==========================================================================
  // GM QUEUE
  // ==========================================================================

  loadQueue: async (campaignId) => {
    set({ isLoading: true });
    try {
      const queue = await trpc.psyche.getQueue.query({ campaignId });
      set({ thoughtQueue: queue, isLoading: false });
    } catch (err) {
      console.error('[PsycheStore] Error loading queue:', err);
      set({ isLoading: false, error: 'Failed to load thought queue' });
    }
  },

  loadHistory: async (campaignId) => {
    try {
      const history = await trpc.psyche.getHistory.query({ campaignId, limit: 50 });
      set({ thoughtHistory: history });
    } catch (err) {
      console.error('[PsycheStore] Error loading history:', err);
    }
  },

  generateThoughts: async (campaignId, activeScene, options = {}) => {
    set({ isGenerating: true, error: null });
    try {
      const { config } = get();
      const result = await trpc.psyche.generateThoughts.mutate({
        campaignId,
        activeScene,
        recentEvents: options.recentEvents || [],
        characterContext: options.characterContext,
        depth: config.depth,
        enabledVoices: config.enabledVoices,
      });

      // Generated thoughts are inserted into DB and arrive via realtime,
      // but also add them to the queue directly for immediate display
      if (result.thoughts.length > 0) {
        set((state) => ({
          thoughtQueue: [...result.thoughts, ...state.thoughtQueue],
        }));
      }

      set({ isGenerating: false });
      return { count: result.count, narratorNote: result.narratorNote };
    } catch (err) {
      console.error('[PsycheStore] Error generating thoughts:', err);
      set({ isGenerating: false, error: 'Failed to generate thoughts' });
      return { count: 0 };
    }
  },

  createThought: async (campaignId, voice, content, options = {}) => {
    try {
      const thought = await trpc.psyche.createThought.mutate({
        campaignId,
        voice,
        content,
        context: options.context,
        intensity: options.intensity || 'thought',
        targetCharacterId: options.targetCharacterId,
        factionRelevance: options.factionRelevance,
      });

      set((state) => ({
        thoughtQueue: [thought, ...state.thoughtQueue],
      }));
    } catch (err) {
      console.error('[PsycheStore] Error creating thought:', err);
      set({ error: 'Failed to create thought' });
    }
  },

  approveThought: async (thoughtId, targetCharacterId) => {
    try {
      const updated = await trpc.psyche.approveThought.mutate({
        thoughtId,
        targetCharacterId,
      });

      set((state) => ({
        thoughtQueue: state.thoughtQueue.map((t) =>
          t.id === thoughtId ? updated : t
        ),
      }));
    } catch (err) {
      console.error('[PsycheStore] Error approving thought:', err);
      set({ error: 'Failed to approve thought' });
    }
  },

  rejectThought: async (thoughtId) => {
    try {
      await trpc.psyche.rejectThought.mutate({ thoughtId });

      set((state) => ({
        thoughtQueue: state.thoughtQueue.filter((t) => t.id !== thoughtId),
      }));
    } catch (err) {
      console.error('[PsycheStore] Error rejecting thought:', err);
      set({ error: 'Failed to reject thought' });
    }
  },

  editThought: async (thoughtId, content, options = {}) => {
    try {
      const updated = await trpc.psyche.editThought.mutate({
        thoughtId,
        content,
        intensity: options.intensity,
        voice: options.voice,
      });

      set((state) => ({
        thoughtQueue: state.thoughtQueue.map((t) =>
          t.id === thoughtId ? updated : t
        ),
      }));
    } catch (err) {
      console.error('[PsycheStore] Error editing thought:', err);
      set({ error: 'Failed to edit thought' });
    }
  },

  deliverThought: async (thoughtId, deliverTo) => {
    try {
      await trpc.psyche.deliverThought.mutate({ thoughtId, deliverTo });

      // Remove from queue on successful delivery
      set((state) => ({
        thoughtQueue: state.thoughtQueue.filter((t) => t.id !== thoughtId),
      }));
    } catch (err) {
      console.error('[PsycheStore] Error delivering thought:', err);
      set({ error: 'Failed to deliver thought' });
    }
  },

  // ==========================================================================
  // PLAYER
  // ==========================================================================

  loadDelivered: async (campaignId, characterId) => {
    try {
      const delivered = await trpc.psyche.getDelivered.query({ campaignId, characterId });
      set({ deliveredThoughts: delivered });
    } catch (err) {
      console.error('[PsycheStore] Error loading delivered thoughts:', err);
    }
  },

  markRead: async (thoughtId) => {
    try {
      await trpc.psyche.markRead.mutate({ thoughtId });

      set((state) => ({
        deliveredThoughts: state.deliveredThoughts.map((t) =>
          t.thoughtId === thoughtId ? { ...t, wasRead: true } : t
        ),
      }));
    } catch (err) {
      console.error('[PsycheStore] Error marking thought read:', err);
    }
  },

  // ==========================================================================
  // REALTIME
  // ==========================================================================

  subscribeToRealtime: (campaignId, characterId) => {
    get().unsubscribeFromRealtime();

    const handlePayload = (payload: { new: Record<string, unknown> }) => {
      const row = payload.new;
      if (!characterId) {
        get().loadQueue(campaignId);
        return;
      }
      if (row.delivered_to === characterId && row.status === 'delivered') {
        get().loadDelivered(campaignId, characterId);
      }
    };

    const { cleanup } = createMultiSubscription({
      channelName: `psyche-${campaignId}`,
      tables: [
        {
          table: 'machina_psyche_thoughts',
          filter: `campaign_id=eq.${campaignId}`,
          event: 'INSERT',
          onPayload: handlePayload,
        },
        {
          table: 'machina_psyche_thoughts',
          filter: `campaign_id=eq.${campaignId}`,
          event: 'UPDATE',
          onPayload: handlePayload,
        },
      ],
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
